import { calculatedValueDisplay } from "@/mixins/calculatedValueDisplay"
import api from "@/services/api"
import { CalculationType, CaseDetails, CaseField, Permission } from "@/types"
import { CaseColumn, getBuiltInCaseColumn, isBuiltInCaseColumn } from "@/types/caseColumns"
import { CurrentUser } from "@/types/user"
import { get } from "lodash"
import Vue from "vue"

export class DataObjectMethods {
	public static async addItem (caseField: CaseField, newValue: any, record: CaseDetails, softEdit: boolean = false): Promise<void> {
		if (record!.id && !softEdit) {
			await api.updateCaseDataField(record!.id, caseField.field.id!, 1, newValue)
		} else {
			if (!caseField.value) {
				Vue.set(caseField, "value", [])
			}

			caseField.value.push(newValue)
			caseField.label = caseField.value.map((i: any) => i.label).join(", ")
		}
	}
	public static async updateItem (caseField: CaseField, updatedValue: any, record: CaseDetails, softEdit: boolean = false): Promise<void> {
		if (record!.id && !softEdit) {
			await api.updateCaseDataField(record!.id, caseField.field.id!, 3, updatedValue)
		} else {
			if (caseField.value) {
				caseField.value.removeWhere((item: any) => item.id === updatedValue.id)
				caseField.value.push(updatedValue)

				caseField.label = caseField.value.map((i: any) => i.label).join(", ")
			}
		}
	}
	public static async removeItem (caseField: CaseField, newValue: any, record: CaseDetails, softEdit: boolean = false): Promise<void> {
		if (record!.id && !softEdit) {
			await api.updateCaseDataField(record!.id, caseField.field.id!, 2, newValue)
		} else {
			if (caseField.value) {
				caseField.value.removeWhere((item: any) => item.id === newValue.id)
				caseField.label = caseField.value.map((i: any) => i.label).join(", ")
			}
		}
	}
}

export function getPermissionTeams (field: CaseField, permissionType: keyof Permission, record: CaseDetails): any[] {
	const sectionPermissions = record.sectionDetails &&
		record.sectionDetails[field.section] &&
		record.sectionDetails[field.section].permissions
		? record.sectionDetails[field.section].permissions
		: null

	if (sectionPermissions) {
		const caseSectionPermissions = record.isCase ? sectionPermissions.case : sectionPermissions.record
		if (caseSectionPermissions && caseSectionPermissions[permissionType] && caseSectionPermissions[permissionType].length > 0) {
			return caseSectionPermissions[permissionType]
		}
	}

	const permissions = field.permissions
		? record.isCase ? field.permissions.case : field.permissions.record
		: null
	if (permissions) {
		return permissions[permissionType]
	}

	if (field.field.permissions) {
		return (record.isCase ? field.field.permissions.case : field.field.permissions.record)[permissionType]
	}

	return []
}

export function hasPermission (field: CaseField, record: CaseDetails, canEditCase: boolean, me: CurrentUser) {
	if (!canEditCase || field.field.displayReadOnly) {
		return false
	}

	const teams = getPermissionTeams(field, "edit", record)
	if (!teams || teams.length === 0) {
		return true
	}

	return me.teams && teams.some(team => me.teams.find((myTeam: any) => myTeam.id === team.id))
}

export function hasViewPermission (field: CaseField, user: CurrentUser, currentCase: CaseDetails): boolean {
	if (!user) return false

	const teams = getPermissionTeams(field, "view", currentCase)
	if (!teams || teams.length === 0) {
		return true
	}

	return user.teams && teams.some(team => user.teams.find((myTeam: any) => myTeam.id === team.id))
}

export function cellValue (column: CaseColumn, caseItem: CaseDetails): any {
	var value:any
	if (column.key.startsWith("calculation.")) {
		// LoDash interperates "." (for calculation.<id>) as a new object
		value = get(caseItem, ["fields", column.key])
	} else {
		value = get(caseItem, column.key)
	}

	if (column.fieldExtensions && column.fieldExtensions.calculationType === CalculationType.DaysUntilOrFromToday) {
		const fieldValue = get(caseItem, "fields." + column.fieldExtensions.dateField)

		if (fieldValue) {
			const fieldValueDate: Date = new Date(fieldValue.value ? fieldValue.value : fieldValue)
			const daysDifference = calculatedValueDisplay.methods.localDaysFromToday(fieldValueDate)

			return { value: daysDifference }
		}
	}

	let label
	if (column.dynamicField && value) {
		label = value.label
	} else if (column.displayKey) {
		label = get(caseItem, column.displayKey)
	}

	const builtInField = isBuiltInCaseColumn(column.key)
		? getBuiltInCaseColumn(column.key)
		: null

	return {
		label: label,
		value: (column.dynamicField ? (value ? value.value : null) : value),
		fieldType: builtInField
			? builtInField.dataType
			: (value?.fieldDataType ?? value?.field?.dataType ?? null)
	}
}
